import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Badge,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../../components/ui/basics";
import CrudModal from "../../components/normative/CrudModal";
import { parseDecimalIT } from "../../utils/parseDecimalIT";

function DistribuzioneModal({ rsa, onClose, onSaved }) {
  const { apiRequest } = useAuth();
  const { data: rsaProfili, loading } = useApi(
    `/normative/rsa-profili?rsa=${encodeURIComponent(rsa.rsa)}`,
  );
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { data: profiliRegione } = useApi(
    `/normative/profili?regione=${encodeURIComponent(rsa.regionecodice)}`,
  );

  React.useEffect(() => {
    if (!profiliRegione || !rsaProfili) return;
    const existing = new Map(
      rsaProfili.map((p) => [p.profilocodice, p.nospiti]),
    );
    setValues(
      Object.fromEntries(
        profiliRegione.map((p) => [p.codice, existing.get(p.codice) ?? ""]),
      ),
    );
  }, [profiliRegione, rsaProfili]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const profiliPayload = Object.entries(values)
        .map(([profilocodice, raw]) => ({
          profilocodice,
          nospiti: parseDecimalIT(raw) || 0,
        }))
        .filter((p) => p.nospiti > 0);
      await apiRequest(
        `/normative/rsa-profili/${encodeURIComponent(rsa.rsa)}`,
        {
          method: "PUT",
          body: JSON.stringify({ profili: profiliPayload }),
        },
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const totale = values
    ? Object.values(values).reduce((s, v) => s + (parseDecimalIT(v) || 0), 0)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            Distribuzione profili — {rsa.rsa}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        {(loading || !values) && <Spinner />}
        {values && (
          <form onSubmit={submit} className="p-4 space-y-3">
            {Object.keys(values).length === 0 && (
              <p className="text-sm text-gray-400">
                Nessun profilo definito per questa regione.
              </p>
            )}
            {Object.keys(values).map((codice) => (
              <label key={codice} className="flex items-center gap-2 text-sm">
                <span className="flex-1">{codice}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values[codice]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [codice]: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1 w-20 text-right"
                />
              </label>
            ))}
            <p className="text-xs text-gray-500">
              Totale: {totale} · occupazione dichiarata: {rsa.occupazione}
              {totale !== rsa.occupazione && (
                <span className="text-amber-600"> ⚠ non coincide</span>
              )}
            </p>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function RsaConfigSection() {
  const { apiRequest } = useAuth();
  const {
    data: configs,
    loading,
    error,
    reload,
  } = useApi("/normative/rsa-configs");
  const { data: regioni } = useApi("/normative/regioni");
  const [modal, setModal] = useState(null); // { mode: "create"|"edit", record? }
  const [distribuzione, setDistribuzione] = useState(null);

  const fields = [
    {
      key: "rsa",
      label: "RSA",
      required: true,
      disabled: modal?.mode === "edit",
    },
    {
      key: "regionecodice",
      label: "Regione",
      type: "select",
      required: true,
      options: (regioni || []).map((r) => ({ value: r.codice, label: r.name })),
    },
    { key: "capacita", label: "Capacità (p.l.)", type: "number" },
    { key: "occupazione", label: "Occupazione dichiarata", type: "number" },
    { key: "oresettimanalifte", label: "Ore/sett FTE", type: "number" },
    {
      key: "dataAvvioPeralba",
      label: "Data avvio Peralba (armonizzazione costo personale)",
      type: "date",
    },
    { key: "note", label: "Note" },
  ];

  const submit = async (values) => {
    const payload = {
      ...values,
      capacita: parseDecimalIT(values.capacita),
      occupazione: parseDecimalIT(values.occupazione),
      oresettimanalifte: parseDecimalIT(values.oresettimanalifte),
    };
    if (modal.mode === "create") {
      await apiRequest("/normative/rsa-configs", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } else {
      await apiRequest(`/normative/rsa-configs/${modal.record.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    }
    reload();
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare la configurazione RSA "${r.rsa}"?`)) return;
    await apiRequest(`/normative/rsa-configs/${r.id}`, { method: "DELETE" });
    reload();
  };

  return (
    <Card
      title="Configurazione RSA"
      actions={
        <button
          onClick={() => setModal({ mode: "create" })}
          className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700"
        >
          + Nuova RSA
        </button>
      }
    >
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {configs && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "rsa", label: "RSA" },
            { key: "regionecodice", label: "Regione" },
            { key: "capacita", label: "Capacità", align: "right" },
            { key: "occupazione", label: "Occupazione", align: "right" },
            {
              key: "oresettimanalifte",
              label: "Ore/sett FTE",
              align: "right",
            },
            {
              key: "dataAvvioPeralba",
              label: "Avvio Peralba",
              render: (r) => r.dataAvvioPeralba || "—",
            },
            {
              key: "attivo",
              label: "Stato",
              render: (r) => (
                <Badge status={r.attivo ? "ok" : "critico"}>
                  {r.attivo ? "Attiva" : "Disattiva"}
                </Badge>
              ),
            },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setDistribuzione(r)}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Distribuzione
                  </button>
                  <button
                    onClick={() => setModal({ mode: "edit", record: r })}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => elimina(r)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Elimina
                  </button>
                </div>
              ),
            },
          ]}
          rows={configs}
        />
      )}

      {modal && (
        <CrudModal
          title={
            modal.mode === "create"
              ? "Nuova RSA"
              : `Modifica ${modal.record.rsa}`
          }
          fields={fields}
          initialValues={modal.record || {}}
          onSubmit={submit}
          onClose={() => setModal(null)}
        />
      )}
      {distribuzione && (
        <DistribuzioneModal
          rsa={distribuzione}
          onClose={() => setDistribuzione(null)}
          onSaved={reload}
        />
      )}
    </Card>
  );
}
